import { createContext, useContext, useEffect, useState, Component } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SettingsProvider } from "@/lib/content";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Home from "@/pages/Home";
import QuemSomos from "@/pages/QuemSomos";
import Areas from "@/pages/Areas";
import AreaDetail from "@/pages/AreaDetail";
import Servicos from "@/pages/Servicos";
import ParaQuem from "@/pages/ParaQuem";
import Palestras from "@/pages/Palestras";
import Conteudo from "@/pages/Conteudo";
import Artigo from "@/pages/Artigo";
import FacaParte from "@/pages/FacaParte";
import Contato from "@/pages/Contato";
import Privacidade from "@/pages/Privacidade";
import AdminApp from "@/pages/admin/AdminApp";

const AuthCtx = createContext({ user: null, checking: true, login: () => {}, logout: () => {} });
export const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);
  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };
  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    setUser(null);
  };
  return <AuthCtx.Provider value={{ user, checking, login, logout }}>{children}</AuthCtx.Provider>;
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-ivory p-8">
          <div className="max-w-md text-center">
            <p className="font-display text-2xl text-navy mb-3">Algo saiu do trilho</p>
            <p className="text-inkgray text-sm mb-6">Ocorreu um erro inesperado. Recarregue a página para continuar.</p>
            <button
              data-testid="error-reload-button"
              onClick={() => window.location.reload()}
              className="bg-navy text-ivory px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-navy-2 transition-colors"
            >
              Recarregar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function SiteLayout() {
  const location = useLocation();
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return (
    <SettingsProvider>
      <Navbar />
      <main>
        <Outlet />
      </main>
      <Footer />
      <a
        data-testid="whatsapp-float-button"
        href="https://wa.me/5519997724509"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Falar pelo WhatsApp"
        className="fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-navy border border-gold/50 text-gold shadow-xl hover:scale-105 transition-transform"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2zm0 18.03c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.26 8.26 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 4.54 0 8.24 3.7 8.24 8.24s-3.7 8.24-8.24 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.81-.78.97-.15.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.29z"/>
        </svg>
      </a>
    </SettingsProvider>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route element={<SiteLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/quem-somos" element={<QuemSomos />} />
              <Route path="/areas-de-atuacao" element={<Areas />} />
              <Route path="/areas-de-atuacao/:slug" element={<AreaDetail />} />
              <Route path="/servicos" element={<Servicos />} />
              <Route path="/para-quem-atuamos" element={<ParaQuem />} />
              <Route path="/palestras-e-treinamentos" element={<Palestras />} />
              <Route path="/conteudo-juridico" element={<Conteudo />} />
              <Route path="/conteudo-juridico/:slug" element={<Artigo />} />
              <Route path="/faca-parte" element={<FacaParte />} />
              <Route path="/contato" element={<Contato />} />
              <Route path="/politica-de-privacidade" element={<Privacidade />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
            <Route path="/admin" element={<AdminApp />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
